import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Avatar,
  Paper,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareIcon from '@mui/icons-material/Share';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getBlogPosts, getFeaturedBlogPosts, getBlogTags } from '../services/blogService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
}));

const FeaturedPostCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
}));

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  // Fetch blog posts and tags on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Fetch all blog posts
        const posts = await getBlogPosts();
        setBlogPosts(posts);
        
        // Fetch featured blog posts
        const featured = await getFeaturedBlogPosts();
        setFeaturedPosts(featured);
        
        // Fetch all tags
        const tags = await getBlogTags();
        setAllTags(tags);
        
        setError(null);
      } catch (err) {
        console.error('Error loading blog data:', err);
        setError('Failed to load blog posts. Please try again later.');
        
        // Fallback data
        setAllTags(["Japan", "Food & Dining", "City Guide", "Sustainable Travel", 
          "Eco-Friendly", "Travel Tips", "Solo Travel", "Female Travelers", 
          "Safety", "Budget Travel", "Luxury", "Travel Hacks", "Digital Nomad", 
          "Remote Work"]);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Filter posts based on search term and selected tag
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTag = selectedTag === '' || post.tags.includes(selectedTag);
    
    return matchesSearch && matchesTag;
  });
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6, mt: { xs: 8, md: 10 } }}>
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Typography 
            variant="h3" 
            component="h1" 
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Travel Blog
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
          >
            Discover travel tips, destination guides, and insider advice to inspire your next adventure.
          </Typography>
          
          {/* Search bar */}
          <TextField
            fullWidth
            placeholder="Search articles..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              maxWidth: 600, 
              mx: 'auto',
              backgroundColor: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 5 }}>{error}</Alert>
        ) : (
          <>
            {/* Category tags */}
            <Box sx={{ mb: 4, overflowX: 'auto', pb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: isMobile ? 'nowrap' : 'wrap', gap: 1, justifyContent: isMobile ? 'flex-start' : 'center', width: isMobile ? 'max-content' : '100%' }}>
                <Chip
                  label="All Categories"
                  clickable
                  color={selectedTag === '' ? "primary" : "default"}
                  onClick={() => setSelectedTag('')}
                  sx={{ mb: isMobile ? 0 : 1 }}
                />
                {allTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    clickable
                    color={selectedTag === tag ? "primary" : "default"}
                    onClick={() => setSelectedTag(tag)}
                    sx={{ mb: isMobile ? 0 : 1 }}
                  />
                ))}
              </Box>
            </Box>
            
            {/* Featured Posts Section (only show on initial view) */}
            {searchTerm === '' && selectedTag === '' && featuredPosts.length > 0 && (
              <Box mb={8}>
                <Typography variant="h5" component="h2" fontWeight="bold" sx={{ mb: 3 }}>
                  Featured Articles
                </Typography>
                
                <Grid container spacing={4}>
                  {featuredPosts.map((post) => (
                    <Grid item xs={12} key={post.id}>
                      <FeaturedPostCard>
                        <CardMedia
                          component="img"
                          sx={{ 
                            width: { xs: '100%', md: '40%' },
                            height: { xs: 240, md: 'auto' },
                            minHeight: { md: 350 },
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                          image={post.image}
                          alt={post.title}
                          onClick={() => navigate(`/blog/${post.id}`)}
                        />
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          p: 3,
                          width: { xs: '100%', md: '60%' },
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                              {post.tags.map((tag, index) => (
                                <Chip 
                                  key={index} 
                                  label={tag} 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTag(tag);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              ))}
                            </Box>
                            <Typography 
                              variant="h5" 
                              component={RouterLink}
                              to={`/blog/${post.id}`}
                              sx={{ 
                                fontWeight: 'bold', 
                                textDecoration: 'none',
                                color: 'text.primary',
                                mb: 1,
                                display: 'block',
                                '&:hover': {
                                  color: 'primary.main'
                                }
                              }}
                            >
                              {post.title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph>
                              {post.excerpt}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar src={post.author.avatar} alt={post.author.name} sx={{ mr: 2 }} />
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {post.author.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(post.date).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })} · {post.readTime}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </FeaturedPostCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* All Posts or Filtered Posts */}
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold" sx={{ mb: 3 }}>
                {searchTerm || selectedTag ? 'Search Results' : 'Latest Articles'}
              </Typography>
              
              {filteredPosts.length > 0 ? (
                <Grid container spacing={3}>
                  {filteredPosts
                    .filter(post => !post.featured || searchTerm !== '' || selectedTag !== '')
                    .map((post) => (
                    <Grid item xs={12} sm={6} md={4} key={post.id}>
                      <StyledCard>
                        <CardMedia
                          component="img"
                          height={220}
                          image={post.image}
                          alt={post.title}
                          sx={{
                            width: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                          onClick={() => navigate(`/blog/${post.id}`)}
                        />
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {post.tags.map((tag, index) => (
                              <Chip 
                                key={index} 
                                label={tag} 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTag(tag);
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                          
                          <Typography 
                            variant="h6" 
                            component={RouterLink}
                            to={`/blog/${post.id}`}
                            sx={{ 
                              fontWeight: 'bold', 
                              textDecoration: 'none',
                              color: 'text.primary',
                              display: 'block',
                              mb: 1,
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            {post.title}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {post.excerpt}
                          </Typography>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar src={post.author.avatar} alt={post.author.name} sx={{ width: 32, height: 32, mr: 1 }} />
                              <Box>
                                <Typography variant="caption" fontWeight="bold">
                                  {post.author.name}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {new Date(post.date).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {post.readTime}
                            </Typography>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    py: 10,
                    px: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 1
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    No articles found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Try adjusting your search terms or filters to find what you're looking for.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedTag('');
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default BlogPage; 